<template>
    <!--
        The navbar strip: `h-(--ui-header-height)` with a bottom hairline, painting
        `--ui-bg-chrome` like the sidebar — THE FRAME IS ONE SURFACE.

        NO PAGE TITLE HERE (as in the company web): the sidebar's lit row already says where you
        are.

        The controls are the legacy portal navbar's: the client-company switch at the LEFT,
        beside the hamburger; the language switch, light/dark and the user menu at the right.
    -->
    <!-- PROJECT DEVIATION: `px-3 sm:px-4 lg:px-6` follows the page gutter rhythm (as in the
         company web) instead of the template's `px-4 sm:px-6`. -->
    <header class="h-(--ui-header-height) shrink-0 flex items-center gap-2 px-3 sm:px-4 lg:px-6 border-b border-default bg-(--ui-bg-chrome)">
        <UButton
            v-if="isMobile"
            color="neutral"
            variant="ghost"
            icon="i-lucide-menu"
            :aria-label="$t('layout.open_menu')"
            @click="openMenu"
        />

        <!-- The client company the contact works for — switching it reloads the portal. -->
        <USelectMenu
            :model-value="selectedClient"
            :items="clientItems"
            value-key="value"
            icon="i-lucide-building-2"
            :search-input="{ placeholder: $t('general.search') }"
            :aria-label="$t('client.select')"
            class="min-w-0 w-44 sm:w-64"
            @update:model-value="selectClient"
        />

        <div class="flex-1" />

        <UDropdownMenu :items="localeItems" :content="{ align: 'end' }">
            <UButton color="neutral" variant="ghost" icon="i-lucide-languages" :label="appStore.locale.toUpperCase()" />
        </UDropdownMenu>

        <!-- Light / dark: the legacy "Change theme" of the user menu, as the house toggle. -->
        <ColorModeToggle />

        <UDropdownMenu :items="profileItems" :content="{ align: 'end' }" :ui="{ content: 'w-56' }">
            <UButton color="neutral" variant="ghost" trailing-icon="i-lucide-chevron-down">
                <UAvatar :alt="fullName" size="2xs" />
                <span class="hidden sm:inline max-w-32 truncate">{{ fullName }}</span>
            </UButton>
        </UDropdownMenu>
    </header>
</template>

<script lang="ts" setup>
    import { computed, onMounted, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import type { DropdownMenuItem } from '@nuxt/ui'

    import { logout } from '@/libs/auth'
    import { LOCALES } from '@/libs/i18n'
    import { PrintError } from '@/libs/toast'
    import ColorModeToggle from '@/layouts/components/ColorModeToggle.vue'
    import { useLayout } from '@/layouts/layout'
    import { useAppStore }   from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import type { ISelectableClient, WebUser } from '@/types/Auth'

    const { t }      = useI18n()
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const { isMobile, openMenu } = useLayout()

    const fullName = computed(() => `${appStore.userData?.name ?? ''} ${appStore.userData?.l_name ?? ''}`.trim())

    // Client company switch --------------------------------------------------
    const clients = ref<ISelectableClient[]>([])

    const clientItems = computed(() =>
        clients.value.map(c => ({ label: c.company_name, value: c.client_company_id })))

    const selectedClient = computed(() => appStore.userData?.client_company_id)

    const loadClients = async () => {
        const { statusCode, data } = await fetchStore.fetch('/clients').get().json<ISelectableClient[]>()
        if (statusCode.value === 200 && data.value) clients.value = data.value
        else PrintError(t('errors.generic_error'))
    }

    // The API re-issues the session cookie for the chosen client and answers with the user
    // (its permissions are per client). The portal then starts over on the task list.
    const selectClient = async (clientCompanyId: number | undefined) => {
        if (clientCompanyId === undefined || clientCompanyId === selectedClient.value) return

        appStore.startLoading()
        const { statusCode, data } = await fetchStore
            .fetch('/selectClient')
            .post({ client_company_id: clientCompanyId })
            .json<WebUser>()
        appStore.stopLoading()

        if (statusCode.value === 200 && data.value?.id) {
            appStore.setLogin(data.value)
            window.location.assign('/task/all')
        } else {
            PrintError(t('client.change_failed'))
        }
    }

    onMounted(loadClients)

    // Colour mode ------------------------------------------------------------
    // The theme is kept per user on the server (GET /changeTheme flips it; the login answer
    // carries it). The toggle switches it here; this follows the switch there.
    watch(() => appStore.colorMode, async (mode, previous) => {
        if (mode === previous) return
        const { statusCode } = await fetchStore.fetch('/changeTheme').get()
        if (statusCode.value !== 200) PrintError(t('errors.generic_error'))
    })

    // Language + user menu ---------------------------------------------------
    const localeItems = computed<DropdownMenuItem[]>(() =>
        LOCALES.map(({ code, name }) => ({
            label:    name,
            icon:     appStore.locale === code ? 'i-lucide-check' : undefined,
            onSelect: () => appStore.changeLocale(code),
        })),
    )

    const profileItems = computed<DropdownMenuItem[][]>(() => [
        [{ label: fullName.value, type: 'label' }],
        [{ label: t('nav.logout'), icon: 'i-lucide-log-out', color: 'error', onSelect: () => logout() }],
    ])
</script>
